import { boxFootprint, toBoxLocal, type Box, type LevelGeometry } from '../level.ts';
import type { Vec3 } from '../math.ts';

/**
 * Where a bot can stand, and how to get from one place to another.
 *
 * Generated from `LevelGeometry` at load rather than authored per arena. Hand-
 * placed waypoints would need doing again for arenas two and three and would
 * drift the moment a wall moved; a grid derived from the geometry is correct by
 * construction and costs nothing to keep in step.
 *
 * Deliberately coarse. Bots do not need to thread a needle — they need to know
 * that the way to the far shrine is through the nave and not through a wall, and
 * a two-metre cell answers that for an eighty-metre arena in sixteen hundred
 * nodes. The steering that actually walks the path smooths over the rest.
 */

/**
 * Cell size, metres. Coarse enough to be cheap, fine enough to find doorways.
 *
 * Halved from 2 when the first authored arena arrived. A cell is only open if
 * its *centre* clears every wall by `CLEARANCE`, so at 2m a doorway needed the
 * better part of four metres before a single cell fell inside it — fine for a
 * hand-written gray-box whose doors were drawn five wide on purpose, and quietly
 * fatal for a level drawn to look like a building. The Great Cathedral's whole
 * Chaos quarter came through sealed: eight pools and a shrine that no bot could
 * path to and no test could see, because the geometry was correct and only the
 * rasterisation disagreed.
 *
 * Four times the cells, and the grid still builds in well under a frame — the
 * binary heap that replaced the linear open-set scan is what buys that.
 */
const CELL = 1;
/**
 * How far a bot's shoulders reach. Cells within this of a wall are refused, so a
 * path never hugs a corner tightly enough for the body to catch on it.
 */
const CLEARANCE = 0.75;
/** Height a blocking box has to reach to actually stop somebody. */
const TORSO = 1.0;

export interface NavGrid {
  readonly cols: number;
  readonly rows: number;
  readonly minX: number;
  readonly minZ: number;
  readonly cell: number;
  /** True where a mage can stand. */
  readonly open: Uint8Array;
}

const index = (g: NavGrid, col: number, row: number): number => row * g.cols + col;

export function colOf(g: NavGrid, x: number): number {
  return Math.floor((x - g.minX) / g.cell);
}

export function rowOf(g: NavGrid, z: number): number {
  return Math.floor((z - g.minZ) / g.cell);
}

export function centreOf(g: NavGrid, col: number, row: number): Vec3 {
  return {
    x: g.minX + (col + 0.5) * g.cell,
    y: 0,
    z: g.minZ + (row + 0.5) * g.cell,
  };
}

export function isOpen(g: NavGrid, col: number, row: number): boolean {
  if (col < 0 || row < 0 || col >= g.cols || row >= g.rows) return false;
  return g.open[index(g, col, row)] === 1;
}

/**
 * Blocks that actually stop a walking mage, at the height one occupies.
 *
 * The band matters in both directions. The Cathedral builds its doorways as a
 * gap with a lintel over it — a wall segment sitting at seven to nine metres —
 * and a test that only asked whether a box reached torso height counted every
 * lintel as a wall and sealed every room in the arena.
 */
function blocking(b: Box): boolean {
  // Props block too: a crate you can walk through is a crate the bots cheat past.
  if (b.kind !== 'wall' && b.kind !== 'pillar' && b.kind !== 'prop') return false;
  // Only the main arena. Valhalla sits forty metres down and nobody paths there.
  if (b.pos.y < -5) return false;

  const bottom = b.pos.y - b.size.y / 2;
  const top = b.pos.y + b.size.y / 2;
  // Has to reach down into the space a body occupies, and up out of the space a
  // foot steps over.
  return bottom < TORSO && top > 0.4;
}

/**
 * Builds the grid.
 *
 * Bounds come from the floor rather than from everything, because ceilings and
 * outer shells extend past the walkable area and would pad the grid with cells
 * nobody can reach.
 */
/**
 * Cells a named doorway or corridor covers, at a relaxed clearance.
 *
 * A doorway in the schema is an *assertion*: the level designer is saying people
 * go through here. Eight of the Great Cathedral's had no open cell at all under
 * the standard 0.75m of shoulder — the rooms either side connected by some
 * longer way round, and every bot in the game took it. Which is not a level
 * fault so much as a grid that refuses to believe a threshold is a threshold.
 *
 * So a named threshold is rasterised at roughly a mage's own width instead. It
 * lets bots path within a hand's breadth of a jamb, which the physics still
 * stops them short of — a slightly scuffed doorway being much the lesser evil
 * against a door nobody uses.
 */
const THRESHOLD_CLEARANCE = 0.42;
const THRESHOLDS: ReadonlySet<string> = new Set(['doorway', 'corridor']);
/**
 * How far outside a threshold plate still counts as being in it.
 *
 * A doorway is drawn as a thin slab across the opening — a third of a metre
 * deep, marking the line rather than the volume. A cell is a metre across and
 * tested at its centre, so most cells that *straddle* a doorway have their
 * centre just outside it and were being rasterised at the strict clearance,
 * which is the whole opening gone. The margin is a cell's half-width and a
 * little: enough that the cells either side of the line believe the name too.
 */
const THRESHOLD_MARGIN = 0.9;

export function buildNavGrid(geometry: LevelGeometry): NavGrid {
  const floors = geometry.boxes.filter((b) => b.kind === 'floor' && b.pos.y > -5);
  const source = floors.length ? floors : geometry.boxes;

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const b of source) {
    // The footprint, not the size: a floor laid at an angle covers a wider
    // rectangle than its own dimensions, and a grid sized to the latter clips
    // the corners off the level.
    const half = boxFootprint(b);
    minX = Math.min(minX, b.pos.x - half.x);
    maxX = Math.max(maxX, b.pos.x + half.x);
    minZ = Math.min(minZ, b.pos.z - half.z);
    maxZ = Math.max(maxZ, b.pos.z + half.z);
  }

  const cols = Math.max(1, Math.ceil((maxX - minX) / CELL));
  const rows = Math.max(1, Math.ceil((maxZ - minZ) / CELL));
  const grid: NavGrid = { cols, rows, minX, minZ, cell: CELL, open: new Uint8Array(cols * rows) };

  const walls = geometry.boxes.filter(blocking);

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const c = centreOf(grid, col, row);
      let free = true;
      /*
       * Inside a named threshold, believe the name.
       */
      const clearance = threshold(geometry, c) ? THRESHOLD_CLEARANCE : CLEARANCE;
      for (const b of walls) {
        /*
         * In the wall's own frame, where an angled wall is an ordinary
         * rectangle again. Squaring angled geometry onto the world axes instead
         * would block the whole bounding box of a diagonal hallway — sealing
         * the corridor the hallway was cut to open.
         */
        const l = toBoxLocal(b, c);
        if (
          Math.abs(l.x) < b.size.x / 2 + clearance
          && Math.abs(l.z) < b.size.z / 2 + clearance
        ) {
          free = false;
          break;
        }
      }
      grid.open[index(grid, col, row)] = free ? 1 : 0;
    }
  }
  return grid;
}

/** Nearest standable cell to a point, for goals that land inside geometry. */
/** Whether a point lies on a floor plate the level called a way through. */
function threshold(geometry: LevelGeometry, at: Vec3): boolean {
  for (const area of geometry.areas ?? []) {
    if (!THRESHOLDS.has(area.kind)) continue;
    const l = toBoxLocal({ pos: area.pos, size: area.size, kind: 'floor', yaw: area.yaw }, at);
    if (
      Math.abs(l.x) <= area.size.x / 2 + THRESHOLD_MARGIN
      && Math.abs(l.z) <= area.size.z / 2 + THRESHOLD_MARGIN
    ) return true;
  }
  return false;
}

export function nearestOpen(g: NavGrid, at: Vec3): { col: number; row: number } | null {
  const c0 = colOf(g, at.x);
  const r0 = rowOf(g, at.z);
  if (isOpen(g, c0, r0)) return { col: c0, row: r0 };

  for (let ring = 1; ring <= 6; ring++) {
    for (let dc = -ring; dc <= ring; dc++) {
      for (let dr = -ring; dr <= ring; dr++) {
        if (Math.abs(dc) !== ring && Math.abs(dr) !== ring) continue;
        if (isOpen(g, c0 + dc, r0 + dr)) return { col: c0 + dc, row: r0 + dr };
      }
    }
  }
  return null;
}

const NEIGHBOURS: readonly (readonly [number, number])[] = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [1, 1], [1, -1], [-1, 1], [-1, -1],
];

/**
 * A* from one point to another, returned as world positions.
 *
 * Diagonal steps are refused when either orthogonal neighbour is closed, or a
 * path will cut the corner of a pillar and walk a bot into it.
 */
/**
 * A* from one point to another, returned as world positions.
 *
 * Diagonal steps are refused when either orthogonal neighbour is closed, or a
 * path will cut the corner of a pillar and walk a bot into it.
 *
 * The open set is a binary heap with a membership flag beside it. The first
 * version scanned the whole open list for the cheapest node and then called
 * `includes` on it again for every neighbour — quadratic twice over, which
 * measured at five milliseconds a call. Eight bots planning on the same tick
 * came to forty, against a frame budget of sixteen.
 */
export function findPath(g: NavGrid, from: Vec3, to: Vec3): Vec3[] {
  const start = nearestOpen(g, from);
  const goal = nearestOpen(g, to);
  if (!start || !goal) return [];

  const startI = index(g, start.col, start.row);
  const goalI = index(g, goal.col, goal.row);
  if (startI === goalI) return [centreOf(g, goal.col, goal.row)];

  const size = g.cols * g.rows;
  const cameFrom = new Int32Array(size).fill(-1);
  const cost = new Float32Array(size).fill(Infinity);
  const closed = new Uint8Array(size);
  const inOpen = new Uint8Array(size);

  // Binary heap of node indices, ordered by cost + heuristic.
  const heap = new Int32Array(size + 1);
  const score = new Float32Array(size);
  let heapSize = 0;

  const push = (node: number): void => {
    let i = heapSize++;
    heap[i] = node;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (score[heap[parent]] <= score[heap[i]]) break;
      const swap = heap[parent];
      heap[parent] = heap[i];
      heap[i] = swap;
      i = parent;
    }
  };

  const pop = (): number => {
    const top = heap[0];
    heap[0] = heap[--heapSize];
    let i = 0;
    for (;;) {
      const l = i * 2 + 1;
      const r = l + 1;
      let best = i;
      if (l < heapSize && score[heap[l]] < score[heap[best]]) best = l;
      if (r < heapSize && score[heap[r]] < score[heap[best]]) best = r;
      if (best === i) break;
      const swap = heap[best];
      heap[best] = heap[i];
      heap[i] = swap;
      i = best;
    }
    return top;
  };

  const heuristic = (i: number): number => {
    const c = i % g.cols;
    const r = (i - c) / g.cols;
    return Math.hypot(c - goal.col, r - goal.row);
  };

  cost[startI] = 0;
  score[startI] = heuristic(startI);
  push(startI);
  inOpen[startI] = 1;

  let found = false;
  while (heapSize > 0) {
    const current = pop();
    inOpen[current] = 0;
    if (current === goalI) {
      found = true;
      break;
    }
    if (closed[current]) continue;
    closed[current] = 1;

    const col = current % g.cols;
    const row = (current - col) / g.cols;

    for (const [dc, dr] of NEIGHBOURS) {
      const nc = col + dc;
      const nr = row + dr;
      if (!isOpen(g, nc, nr)) continue;
      // No squeezing between two blocked cells diagonally.
      if (dc !== 0 && dr !== 0 && (!isOpen(g, col + dc, row) || !isOpen(g, col, row + dr))) {
        continue;
      }
      const ni = index(g, nc, nr);
      if (closed[ni]) continue;

      const step = dc !== 0 && dr !== 0 ? Math.SQRT2 : 1;
      const next = cost[current] + step;
      if (next >= cost[ni]) continue;

      cost[ni] = next;
      cameFrom[ni] = current;
      score[ni] = next + heuristic(ni);
      // A cheaper route to a node already queued is pushed again rather than
      // sifted into place; the stale copy is skipped by the closed check above.
      if (!inOpen[ni]) {
        push(ni);
        inOpen[ni] = 1;
      }
    }
  }

  if (!found) return [];

  const path: Vec3[] = [];
  let at = goalI;
  while (at !== -1 && at !== startI) {
    const c = at % g.cols;
    const r = (at - c) / g.cols;
    path.push(centreOf(g, c, r));
    at = cameFrom[at];
  }
  return path.reverse();
}


/**
 * Standing spots near a point that have something solid at their back.
 *
 * A cell counts as cover if at least one of its neighbours is closed — a wall,
 * a pillar, the edge of a room. That is a crude definition and the right one
 * here: what a Cleric wants is not a firing position but a place where they can
 * be approached from fewer directions and can step out of sight, which is
 * exactly what standing against something gives them.
 *
 * Returned nearest-first so a caller can take a different one per bot and have
 * them all still be near the thing they are guarding.
 */
export function coverNear(g: NavGrid, at: Vec3, radius: number): Vec3[] {
  const found: { pos: Vec3; d: number }[] = [];
  const reach = Math.ceil(radius / g.cell);
  const c0 = colOf(g, at.x);
  const r0 = rowOf(g, at.z);

  for (let dc = -reach; dc <= reach; dc++) {
    for (let dr = -reach; dr <= reach; dr++) {
      const col = c0 + dc;
      const row = r0 + dr;
      if (!isOpen(g, col, row)) continue;

      const against = NEIGHBOURS.some(([nc, nr]) => !isOpen(g, col + nc, row + nr));
      if (!against) continue;

      const pos = centreOf(g, col, row);
      const d = Math.hypot(pos.x - at.x, pos.z - at.z);
      // Not on top of it, and not so far as to have abandoned it.
      if (d > radius || d < g.cell) continue;
      found.push({ pos, d });
    }
  }

  found.sort((a, b) => a.d - b.d);
  return found.map((f) => f.pos);
}
